import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef, useCallback, useRef, type ReactNode } from 'react';
import { useEditPanelViewport } from './use-edit-panel-viewport.js';

/** Shared content layout; the caller owns the Radix root, mutations and close guards. */
type CompactDialogContentProps = {
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  onClose(): void;
  busy?: boolean;
  className?: string;
  closeLabel?: string;
  restoreFocus?(): void;
};

export const CompactDialogContent = forwardRef<HTMLDivElement, CompactDialogContentProps>(
  function CompactDialogContent(
    {
      title,
      description,
      children,
      footer,
      onClose,
      busy = false,
      className = '',
      closeLabel = 'Close dialog',
      restoreFocus,
    },
    forwardedRef,
  ) {
    const { panelRef, bodyRef } = useEditPanelViewport(true);
    const capturePanel = useCallback(
      (node: HTMLDivElement | null) => {
        panelRef(node);
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [panelRef, forwardedRef],
    );
    const close = useRef<HTMLButtonElement>(null);
    const returnFocus = useRef<HTMLElement | null>(null);
    return (
      <Dialog.Content
        ref={capturePanel}
        className={`shoot-destructive dui-compact-panel ${className}`}
        data-compact-panel
        {...(!description ? { 'aria-describedby': undefined } : {})}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          returnFocus.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
          close.current?.focus({ preventScroll: true });
        }}
        onCloseAutoFocus={(event) => {
          if (restoreFocus) {
            event.preventDefault();
            restoreFocus();
          } else if (returnFocus.current?.isConnected) {
            event.preventDefault();
            returnFocus.current.focus({ preventScroll: true });
          }
        }}
        onPointerDownOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => {
          if (busy) event.preventDefault();
        }}
      >
        <header data-panel-header>
          <Dialog.Title>{title}</Dialog.Title>
          <button
            ref={close}
            type="button"
            className="shoot-button shoot-button--outline shoot-button--icon"
            aria-label={closeLabel}
            disabled={busy}
            onClick={onClose}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>
        <div ref={bodyRef} data-panel-body>
          {description ? <Dialog.Description>{description}</Dialog.Description> : null}
          {children}
        </div>
        {footer ? <footer data-panel-footer>{footer}</footer> : null}
      </Dialog.Content>
    );
  },
);
